// Catalog.ID Client — Shared With Me Pane
// ============================================================================
// Lists claims shared with the current identity by other identities.
// Each claim can be decrypted using the current identity's attr_encrypt key.

import {
    createListPaneType, createElement, createFieldGroup, createFormRow, createEmptyState,
    renderPaneActions,
} from '@catalog-shell';
import type {
    ListPaneConfig, PaneActionDescriptor, PaneInstance, PaneType, PaneTypeRenderContext,
} from '@catalog-shell';
import { DataSource } from '@catalog-shell/components';
import type { ColumnDef, FetchResult } from '@catalog-shell/components';
import { claimTypeLabel } from './claims-list-pane';
import * as claimsApi from '../api/claims';
import * as trustApi from '../api/trust';
import { renderClaimStatusSection } from '../components/claim-status-section';
import { signEndorsement } from '../crypto/trust';
import { decryptClaim, decryptLabel, unwrapSymmetricKey } from '../crypto/claims';
import * as aes from '../crypto/aes';
import { keys } from '../state/keys';
import { identity } from '../state/identity';
import type { SharedClaimSummary } from '../api/types';
import { PANE_IDS } from '../shell-config';

// ============================================================================
// List pane
// ============================================================================

const columns: ColumnDef<SharedClaimSummary>[] = [
    {
        key: 'claimType',
        header: 'Type',
        width: 120,
        getValue: (item) => item.claimType,
        type: { kind: 'text', converter: (v) => claimTypeLabel(v as string) },
    },
    {
        key: 'ownerCid',
        header: 'From',
        width: 140,
        getValue: (item) => item.ownerCid,
        type: { kind: 'text' },
    },
    {
        key: 'encryptedLabel',
        header: 'Label',
        width: 140,
        getValue: (item) => item.encryptedLabel,
        type: { kind: 'text', converter: (v) => v ? '(encrypted)' : '—', textColor: () => '#555' },
    },
    {
        key: 'creationTime',
        header: 'Shared',
        width: 100,
        sortable: true,
        getValue: (item) => item.creationTime,
        type: {
            kind: 'text',
            converter: (v) => {
                try { return new Date(v as string).toLocaleDateString(); }
                catch { return String(v); }
            },
        },
    },
];

function createSharedDataSource(): DataSource<SharedClaimSummary> {
    return new DataSource<SharedClaimSummary>({
        fetch: async (params): Promise<FetchResult<SharedClaimSummary>> => {
            const shared = await claimsApi.listSharedClaims();
            const sliced = shared.slice(params.offset, params.offset + params.limit);
            return { items: sliced, totalCount: shared.length };
        },
        keyFn: (item) => item.claimUuid,
        pageSize: 100,
    });
}

const config: ListPaneConfig<SharedClaimSummary> = {
    typeId: PANE_IDS.sharedWithMe,
    defaultTitle: 'Shared With Me',
    createDataSource: () => createSharedDataSource(),
    columns,
    keyFn: (item) => item.claimUuid,
    initialMode: 'table',
    enabledModes: ['table'],
    selectionMode: 'none',
    onCreated: ({ dataSource }, ctx) => {
        ctx.disposables.add(ctx.shell.on('data-changed', (data) => {
            if ((data as { type?: string })?.type === 'claims') {
                dataSource.refresh();
            }
        }));
    },
    onItemSelect: (item, ctx) => {
        ctx.surface?.openPane({
            typeId: PANE_IDS.sharedClaimView,
            title: `${claimTypeLabel(item.claimType)} from ${item.ownerCid}`,
            context: { sharedClaim: item },
        }, { originPaneId: ctx.instance.instanceId });
    },
    ariaLabel: 'Claims shared with me',
};

export function createSharedWithMePane() {
    return createListPaneType(config);
}

// ============================================================================
// Shared claim detail view (decrypt + display)
// ============================================================================

// ----------------------------------------------------------------------------
// Title-row actions
//
// Endorsing or disputing a shared claim is a verb about the claim on screen, so
// it rides in the pane's title row (shell USAGE, "Where a button belongs") while
// the body keeps the explanation of what an endorsement is for. The row stays
// empty until BOTH gates have settled: the viewer must be a personal identity at
// trust tier 1 or above, and the endorsement they may already hold must have
// been read back — the plan is Endorse/Dispute or Withdraw, never both.
// ----------------------------------------------------------------------------

/** What the title row needs to know about this viewer and this claim. */
export interface SharedClaimActionState {
    /** Eligible viewer AND the existing endorsement has been read back. */
    endorsable: boolean;
    /** The endorsement on record: true endorsed, false disputed, null none. */
    endorsement: boolean | null;
    /** A submission is in flight. */
    busy: boolean;
}

type EndorseAction = 'endorse' | 'dispute' | 'withdraw';

interface SharedClaimState extends SharedClaimActionState {
    ctx: PaneTypeRenderContext;
    claim: SharedClaimSummary;
    /** Pane-owned status/error lines, filled in with the endorsement section. */
    showState: () => void;
    showError: (message: string) => void;
    clearError: () => void;
}

const states = new WeakMap<PaneInstance, SharedClaimState>();

const SUBMITTING = 'Submitting your endorsement…';

/** Pure: which endorsement verbs this viewer is offered. */
export function planSharedClaimActions(
    st: SharedClaimActionState | undefined,
    exec: (action: EndorseAction) => void,
): PaneActionDescriptor[] {
    if (!st?.endorsable) return [];
    const busy = { disabled: st.busy, title: st.busy ? SUBMITTING : undefined };
    if (st.endorsement === null) {
        return [
            { key: 'endorse', label: 'Endorse', variant: 'primary', ...busy, execute: () => exec('endorse') },
            { key: 'dispute', label: 'Dispute', variant: 'danger', ...busy, execute: () => exec('dispute') },
        ];
    }
    return [{ key: 'withdraw', label: 'Withdraw', ...busy, execute: () => exec('withdraw') }];
}

function sharedClaimActions(instance: PaneInstance): HTMLElement[] {
    const st = states.get(instance);
    return renderPaneActions(planSharedClaimActions(st, (action) => {
        void submitEndorsement(st!, action);
    }));
}

async function submitEndorsement(st: SharedClaimState, action: EndorseAction): Promise<void> {
    if (st.busy) return;
    st.busy = true;
    st.clearError();
    st.ctx.shell.refreshPaneActions(st.ctx.instance.instanceId);
    try {
        if (action === 'withdraw') {
            await trustApi.removeEndorsement(st.claim.claimUuid);
            st.endorsement = null;
        } else {
            const value = action === 'endorse';
            const sig = signEndorsement(st.claim.claimUuid, value);
            await trustApi.endorseClaim(st.claim.claimUuid, value, sig);
            st.endorsement = value;
        }
        st.showState();
    } catch (e) {
        st.showError(e instanceof Error
            ? e.message
            : action === 'withdraw' ? 'Withdraw failed' : 'Endorsement failed');
    } finally {
        st.busy = false;
        st.ctx.shell.refreshPaneActions(st.ctx.instance.instanceId);
    }
}

export function createSharedClaimViewPane(): PaneType {
    return {
        typeId: PANE_IDS.sharedClaimView,
        kind: 'leaf',
        defaultTitle: 'Shared Claim',
        capabilities: [],
        tabBarActions: sharedClaimActions,
        onDestroy: (instance) => { states.delete(instance); },
        render: (ctx: PaneTypeRenderContext) => {
            const container = createElement('div', { className: 'cid-pane-content' });
            const claim = ctx.instance.context['sharedClaim'] as SharedClaimSummary | undefined;
            if (!claim) {
                container.appendChild(createEmptyState({ message: 'No claim selected' }));
                return container;
            }
            const st: SharedClaimState = {
                ctx,
                claim,
                endorsable: false,
                endorsement: null,
                busy: false,
                showState: () => {},
                showError: () => {},
                clearError: () => {},
            };
            states.set(ctx.instance, st);
            void renderSharedClaim(container, st);
            return container;
        },
    };
}

async function renderSharedClaim(container: HTMLElement, st: SharedClaimState): Promise<void> {
    const { ctx, claim } = st;
    const infoGroup = createFieldGroup({ title: 'Shared Claim' });

    const typeRow = createFormRow({ label: 'Type' });
    typeRow.content.appendChild(createElement('span', { textContent: claimTypeLabel(claim.claimType) }));
    infoGroup.body.appendChild(typeRow.element);

    const fromRow = createFormRow({ label: 'From' });
    fromRow.content.appendChild(createElement('span', { className: 'cid-profile-cid', textContent: claim.ownerCid }));
    infoGroup.body.appendChild(fromRow.element);

    // Decrypt value and label
    const attrKey = keys.getPrivateKey('attr_encrypt');
    if (attrKey && claim.encryptedLabel && claim.labelNonce) {
        try {
            const symKey = await unwrapSymmetricKey(aes.fromBase64(claim.encryptedKey), aes.fromBase64(attrKey));
            const labelText = await decryptLabel(claim.encryptedLabel, claim.labelNonce, symKey);
            if (labelText) {
                const labelRow = createFormRow({ label: 'Label' });
                labelRow.content.appendChild(createElement('span', { textContent: labelText }));
                infoGroup.body.appendChild(labelRow.element);
            }
        } catch { /* label decryption failed — skip */ }
    }

    const valueRow = createFormRow({ label: 'Value' });
    if (attrKey) {
        try {
            const decrypted = await decryptClaim(
                claim.encryptedValue, claim.nonce, claim.encryptedKey, aes.fromBase64(attrKey),
            );
            valueRow.content.appendChild(createElement('span', { textContent: decrypted }));
        } catch {
            valueRow.content.appendChild(createElement('span', {
                className: 'cid-pane-error cid-pane-error--visible',
                textContent: '[decryption failed]',
            }));
        }
    } else {
        valueRow.content.appendChild(createElement('span', {
            textContent: '[keys not loaded]',
        }));
    }
    infoGroup.body.appendChild(valueRow.element);

    const dateRow = createFormRow({ label: 'Shared' });
    dateRow.content.appendChild(createElement('span', {
        textContent: new Date(claim.creationTime).toLocaleString(),
    }));
    infoGroup.body.appendChild(dateRow.element);

    container.appendChild(infoGroup.element);

    // Validation status and certificates (fetch full claim detail)
    try {
        const detail = await claimsApi.getClaim(claim.claimUuid);
        renderClaimStatusSection(container, detail);
    } catch { /* claim detail not accessible — skip status section */ }

    // Endorsement section (only for personal identities with sufficient trust
    // tier). The buttons themselves are in the title row; what stays here is the
    // explanation, the status line and the error line.
    if (identity.isPersonal()) {
        const myCid = identity.getActiveCid();
        if (myCid) {
            void trustApi.getTrustScore(myCid).then(async score => {
                if (score.tier < 1) return; // not eligible to endorse

                const endorseGroup = createFieldGroup({ title: 'Endorsement' });
                const endorseNote = createElement('p', {
                    className: 'cid-pane-note cid-pane-note--flush',
                    textContent: 'Endorse this claim to vouch for its accuracy. The claim owner needs endorsements to publish certain claim types.',
                });
                endorseGroup.body.appendChild(endorseNote);

                const endorseError = createElement('div', { className: 'cid-pane-error' });
                const endorseStatus = createElement('div', { className: 'cid-trust-success' });
                endorseGroup.body.appendChild(endorseError);
                endorseGroup.body.appendChild(endorseStatus);
                container.appendChild(endorseGroup.element);

                st.showState = () => {
                    endorseStatus.textContent = st.endorsement === null
                        ? ''
                        : st.endorsement ? 'You endorsed this claim.' : 'You disputed this claim.';
                };
                st.showError = (message) => {
                    endorseError.textContent = message;
                    endorseError.classList.add('cid-pane-error--visible');
                };
                st.clearError = () => { endorseError.classList.remove('cid-pane-error--visible'); };

                // Load the endorsement already on record: it decides whether the
                // row offers Endorse/Dispute or Withdraw.
                const existing = await trustApi.getMyEndorsement(claim.claimUuid);
                st.endorsement = existing ? existing.endorsement : null;
                st.endorsable = true;
                st.showState();
                ctx.shell.refreshPaneActions(ctx.instance.instanceId);
            }).catch(() => { /* no trust score available — skip endorsement section */ });
        }
    }
}
